import { useCallback, useEffect, useState } from 'react';
import { Check, UserPlus, X } from 'lucide-react';
import { db } from '@/lib/db';
import type { GroupRoundJoinRequest } from '@/lib/db/types';

interface Props {
  roundId: string;
  /** Only the organizer can act on requests. */
  enabled: boolean;
  /** Called after a request is accepted so the parent can reload the roster. */
  onChanged?: () => void;
}

export function JoinRequestsPanel({ roundId, enabled, onChanged }: Props) {
  const [requests, setRequests] = useState<GroupRoundJoinRequest[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const rows = await db.groupRounds.listJoinRequests(roundId);
      setRequests(rows);
    } catch {
      // non-critical; leave list as-is
    }
  }, [roundId, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const respond = async (req: GroupRoundJoinRequest, accept: boolean) => {
    setBusyId(req.id);
    setError(null);
    try {
      await db.groupRounds.respondJoin(req.id, accept);
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
      if (accept) onChanged?.();
    } catch (err) {
      setError((err as Error).message || 'Could not update request');
    } finally {
      setBusyId(null);
    }
  };

  if (!enabled || requests.length === 0) return null;

  return (
    <div className="rounded-xl border border-gold/40 bg-gold/5 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-extrabold tracking-wide text-gold-deep">
        <UserPlus size={14} />
        JOIN REQUESTS ({requests.length})
      </div>
      <div className="space-y-2">
        {requests.map((req) => (
          <div key={req.id} className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
              {req.requester_name || 'Player'}
              <span className="ml-1 text-xs font-normal text-mute">wants to join</span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                disabled={busyId === req.id}
                onClick={() => void respond(req, true)}
                className="flex h-8 items-center gap-1 rounded-lg bg-navy px-2.5 text-xs font-bold text-cream disabled:opacity-60"
              >
                <Check size={14} /> Accept
              </button>
              <button
                type="button"
                disabled={busyId === req.id}
                onClick={() => void respond(req, false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-paper text-mute disabled:opacity-50"
                aria-label="Decline request"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {error && <div className="mt-2 text-xs font-semibold text-bad">{error}</div>}
    </div>
  );
}
